"use client";

import React from "react";
import { getImageProps } from "next/image";

import type { GROQImage } from "@/sanity/lib/definitions";

/**
 * Warms the browser cache for images that only get rendered further down a page.
 *
 * next/image starts a lazy image a few hundred pixels before it reaches the
 * viewport, which is enough for a photo that fades in on its own once it is
 * there — but not for one whose reveal is scrubbed off scroll position. Those
 * are already mid-reveal when the request goes out, so a card can cross the
 * screen as an empty frame and then pop. The Bastian Keller gallery is the worst
 * case: ten full-bleed photos at quality 100, each revealed as you travel past.
 *
 * So they are fetched ahead of time, off the main load path — nothing starts
 * until the page has finished loading and the browser is idle, and the fetches
 * run a few at a time so the first photos land early rather than everything
 * sharing the connection and arriving together.
 *
 * URLs come from `getImageProps`, which is the same code `<Image>` runs to build
 * the `src`/`srcset` it renders. Assembling the optimizer URL by hand would work
 * right until a quality or deviceSizes change quietly made the prefetched URL a
 * different one, at which point every photo would download twice instead of none.
 */

/** An image to warm, with the quality of the component that renders it. */
export type PrefetchImage = {
  image?: GROQImage;
  /** Must match the `quality` prop at the render site, or the URLs won't line up. */
  quality?: number;
};

/**
 * How many are in flight at once. Enough to keep the connection busy, few enough
 * that the list is worked through in order: fetching all of them together would
 * mean the photo you reach first finishes no sooner than the one you never reach.
 */
const CONCURRENCY = 3;

/**
 * Also the fallback delay where requestIdleCallback is missing (Safari). Idle
 * time never comes on a page that animates continuously, so the timeout is what
 * actually starts this most of the time.
 */
const IDLE_TIMEOUT = 2000;

/** Connections where pulling down photos nobody asked for isn't a favour. */
const SLOW_CONNECTIONS = ["slow-2g", "2g"];

type NetworkInformation = { saveData?: boolean; effectiveType?: string };

function shouldPrefetch() {
  const connection = (navigator as Navigator & { connection?: NetworkInformation })
    .connection;

  if (!connection) return true;
  if (connection.saveData) return false;

  return !SLOW_CONNECTIONS.includes(connection.effectiveType || "");
}

/**
 * Resolves once the image is in the cache. The element is never inserted into the
 * document — the fetch is the point, and the decoded result is what the real
 * `<img>` picks up when it renders the same URL.
 */
function warm(target: PrefetchImage) {
  return new Promise<void>((resolve) => {
    if (!target.image?.src) {
      resolve();
      return;
    }

    const { props } = getImageProps({
      src: target.image,
      alt: "",
      quality: target.quality,
    });

    const element = new window.Image();
    element.decoding = "async";
    // set as an attribute rather than a property: the DOM typings this project
    // builds against don't have fetchPriority yet
    element.setAttribute("fetchpriority", "low");

    // a photo that fails here fails again at the render site, which is where it
    // has somewhere to report to — the queue just moves on
    element.onload = () => resolve();
    element.onerror = () => resolve();

    // sizes first: it feeds the candidate selection that setting srcset kicks off
    if (props.sizes) element.sizes = props.sizes;
    if (props.srcSet) element.srcset = props.srcSet;
    element.src = props.src;
  });
}

/**
 * Prefetches `images` in order, starting once the page has settled. Safe to hand
 * a freshly built array on every render — the work is keyed off the URLs, so it
 * only reruns when the set of images itself changes.
 */
export function useImagePrefetch(images: PrefetchImage[], enabled: boolean = true) {
  const key = images.map((entry) => entry.image?.src ?? "").join("|");

  const queue = React.useMemo(
    () => images.filter((entry) => entry.image?.src),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );

  React.useEffect(() => {
    if (!enabled || queue.length === 0 || !shouldPrefetch()) return;

    let cancelled = false;

    const run = () => {
      let cursor = 0;

      const next = (): Promise<void> => {
        const target = cancelled ? undefined : queue[cursor++];
        if (!target) return Promise.resolve();

        return warm(target).then(next);
      };

      // one chain per slot, each taking the next image as it frees up
      for (let slot = 0; slot < Math.min(CONCURRENCY, queue.length); slot++) {
        void next();
      }
    };

    let cancelSchedule = () => {};

    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        const id = window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT });
        cancelSchedule = () => window.cancelIdleCallback(id);
        return;
      }

      const id = window.setTimeout(run, IDLE_TIMEOUT);
      cancelSchedule = () => window.clearTimeout(id);
    };

    // nothing here is more urgent than the page it is being prefetched for
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
      cancelSchedule = () => window.removeEventListener("load", schedule);
    }

    return () => {
      cancelled = true;
      cancelSchedule();
    };
  }, [queue, enabled]);
}
